import { EventEmitter, Injectable } from '@angular/core';
import { ColorDeciderService } from './color-decider.service';
import { MimicActionsService } from './mimic-actions.service';
import { ColorAnimationService } from './color-animation.service';
import { ChannelType, isChannelMotorType, isDigital, isRunningHour } from '../models/channelType';
import { FlagsService } from './channel-flags.service';
import {
    addHandler,
    clearHandlers,
    click,
    disableStatusUpdated,
    flashingStatusUpdated,
    updatedChannelEvent,
    visualStateEvent,
} from '../models/events';
import { ValveService } from './valve-service';
import { PumpService } from './pump-service';
import { FanService } from './fan-service';
import { GaugeService } from './gauge-service.sevice';
import { ControlElementService } from './control-elements.service';

enum CanvasInputMode {
    SELECT = 'select',
    GROUP_SELECTED = 'groupSelected',
    UNGROUP_SELECTED = 'ungroupSelected',
    DUPLICATE = 'duplicate',
    MULTISELECT = 'multiselect',
    MOVE_CANVAS = 'moveCanvas',
    COMMENT = 'comment',
    TEXT = 'text',
    TEXT_EDIT = 'textedit',
    CONNECTOR = 'connector',
    PATH_EDIT = 'pathedit',
    TEMPLATE_INSERT = 'insert_template',
    EYE_DROPPER = 'eyeDropper',
    RECTANGLE = 'rect',
    ELLIPSE = 'ellipse',
    POLYGON = 'polygon',
    FREE_DRAW = 'free_draw',
    LINE = 'line',
    ARROW = 'arrow',
    IMAGE = 'image',
    ADD_SVG = 'add_svg',
    INPUT_VALUE = 'inputValue',
    OUTPUT_VALUE = 'outputValue',
    BUTTON = 'button',
    STATUS_INDICATOR = 'status',
    TOGGLE = 'toggleSwitch',
    BAR = 'bar',
    PATH = 'path',
    TRIANGLE = 'triangle',
    CONNECTOR_STRAIGHT = 'connector-straight',
    CONNECTOR_CORNER = 'connector-corner',
    CONNECTOR_DIRECTION = 'connector-direction',
    CONNECTOR_ENDPOINT = 'connector-endpoint',
    CONNECTOR_JUNCTION = 'connector-junction',
    DEFAULT_VALVE = 'default-valve',
    DEFAULT_PUMP = 'default-pump',
    DEFAULT_FAN = 'default-fan',
    DEFAULT_TANK = 'default-tank',
    DEFAULT_SWITCH = 'default-switch',
    GAUGE = 'gauge',
}

export enum TankTypes {
    default = 'Default',
    mainAirReserve = 'Main air reserve',
    auxiliaryAirReserve = 'Auxiliary air reserve',
}

@Injectable({
    providedIn: 'root',
})
export class ElementsEventListenerService {
    private _onCreateLibraryComponentElement = new EventEmitter();

    constructor(
        private colorDeciderService: ColorDeciderService,
        private mimiActionsService: MimicActionsService,
        private colorAnimationService: ColorAnimationService,
        private flagsService: FlagsService,
        private valveService: ValveService,
        private gaugeService: GaugeService,
        private controlElementsService: ControlElementService,
        private pumpService: PumpService,
        private fanService: FanService
    ) {}

    private scaleValue(channel, value: number) {
        return value / channel?.fcuData?.scale;
    }

    async attachEventListerners(elem, elementSetting, elementIntegration, vesselId, channelList, defaultColors) {
        if (!elementSetting) {
            this.addVisualStateEventListener(elem, elementIntegration);
            return;
        }

        const channel = channelList.find(channel => channel.rowId === elementSetting.channelTag);

        const iCreateType = elem.getAttribute('data-icreate-type');
        if (iCreateType === CanvasInputMode.DEFAULT_VALVE) {
            this.valveService.addDefaultValveListener(elem, elementSetting);
            return;
        } else if (iCreateType === CanvasInputMode.DEFAULT_PUMP) {
            this.pumpService.addDefaultPumpListener(elem, elementSetting);
            return;
        } else if (iCreateType === CanvasInputMode.DEFAULT_FAN) {
            this.fanService.addDefaultFanListener(elem, elementSetting);
            return;
        } else if (iCreateType === CanvasInputMode.GAUGE) {
            const channel = channelList.find(channel => channel.rowId == elementSetting.channelTag);
            this.gaugeService.drawAndGetLiveInstance(channel, elem);
            return;
        }

        if (elem.nodeName.toLowerCase() == 'foreignobject') {
            const elementType = elem.getAttribute('type');
            if (elementType === CanvasInputMode.BAR) {
                this.controlElementsService.updateBarDisplay(elem, channel, defaultColors);
                this.addBarEvents(elem, elementSetting, channel);
            } else if (elementType === CanvasInputMode.BUTTON) {
                this.addButtonEvents(elem, elementSetting, channel);
            } else if (elementType === CanvasInputMode.INPUT_VALUE || elementType === CanvasInputMode.OUTPUT_VALUE) {
                this.updateValueUnits(elem, channel);
                this.addInputEventListeners(elem, elementSetting, channel);
            } else if (elementType === CanvasInputMode.STATUS_INDICATOR) {
                this.addStatusIndicatorEvents(elem, elementSetting, vesselId);
            } else if (elementType === CanvasInputMode.TOGGLE) {
                this.addToggleEvents(elem, elementSetting);
            }
        } else {
            this.addNormalRectListener(elem, elementSetting, channel);
        }
    }

    updateValueUnits(elem, channel) {
        const elementType = elem.getAttribute('type');
        if (!channel || (elementType !== CanvasInputMode.INPUT_VALUE && elementType !== CanvasInputMode.OUTPUT_VALUE))
            return;
        this.controlElementsService.udpateInputUnit(elem, channel.opsData?.measurementUnit);
    }

    async addNormalRectListener(elem, elementSetting, channel) {
        const elementId = elem.getAttribute('id');

        if (channel) {
            let isMotor = false;

            if (elementSetting.channelIsDigital) {
                isMotor = isChannelMotorType(channel.fcuData.channelType);
            }

            const handler = async (event: CustomEvent) => {
                const channelValue = event.detail.value;
                const flag = event.detail;

                const elementClasses = this.colorAnimationService.getClassesByFlag(flag);
                if (this.colorAnimationService.hasFlagStatus(elementClasses)) {
                    this.colorDeciderService.setAlarmFlagsColor(elementClasses, elem);
                    return;
                }
                this.colorDeciderService.stopAlarmFlagsColor(elementId);
                let color = this.colorDeciderService.getColorRanges(elementSetting.ranges, channelValue);
                if (color) {
                    if (this.shouldUpdateColor(color, elementId)) {
                        this.colorDeciderService.currentFills.set(elementId, color);
                        elem.setAttribute('fill', color);
                    }
                    return;
                }
                if (elementSetting.channelIsDigital) {
                    color = isMotor
                        ? this.colorDeciderService.getMotorChannelColor(channelValue)
                        : this.colorDeciderService.getDigitalContactChannelColor(channelValue);
                } else {
                    color = this.colorDeciderService.getDefaultColor();
                }
                if (!this.shouldUpdateColor(color, elementId)) {
                    return;
                }
                this.colorDeciderService.currentFills.set(elementId, color);
                elem.setAttribute('fill', color);
            };

            addHandler(elem, updatedChannelEvent, handler);
        }
    }

    async addBarEvents(elem, elementSetting, channel) {
        const indicatorPointer = elem.querySelector('[indicatorPointer="true"]') as HTMLElement;
        const valueContainer = elem.querySelector('[valueContainer="true"]') as HTMLElement;

        const measurableRange = channel.fcuData.measurableRange;
        const normalRange = channel.fcuData.normalRange;
        const rangeMax = this.scaleValue(channel, measurableRange.max);
        const rangeMin = this.scaleValue(channel, measurableRange.min);
        const normalRangeMax = this.scaleValue(channel, normalRange.max);
        const normalRangeMin = this.scaleValue(channel, normalRange.min);

        const elementId = elem.getAttribute('id');

        const handler = async (event: CustomEvent) => {
            const channelValue = event.detail.value;
            const maxUnitsValue = rangeMax - rangeMin;
            const scaledValue = this.scaleValue(channel, parseFloat(channelValue));
            const percentage = Math.round((scaledValue / Number(maxUnitsValue)) * 100);

            const flag = event.detail;

            indicatorPointer.style.top = 100 - percentage + '%';
            valueContainer.textContent = `${scaledValue} (${channel.opsData.measurementUnit})`;

            const elementClasses = this.colorAnimationService.getClassesByFlag(flag);
            if (this.colorAnimationService.hasFlagStatus(elementClasses)) {
                this.colorDeciderService.setAlarmFlagsColor(elementClasses, elem, indicatorPointer);
                return;
            }
            this.colorDeciderService.stopAlarmFlagsColor(elementId);
            let color = this.colorDeciderService.getColorRanges(elementSetting.ranges, channelValue);
            if (color) {
                if (this.shouldUpdateColor(color, elementId)) {
                    this.colorDeciderService.currentFills.set(elementId, color);
                    indicatorPointer.style.backgroundColor = color;
                }
                return;
            }
            if (scaledValue < normalRangeMax && scaledValue > normalRangeMin) {
                color = this.colorDeciderService.getDefaultBarColor();
            } else color = '#133459';
            indicatorPointer.style.backgroundColor = color;
            this.colorDeciderService.currentFills.set(elementId, color);
        };

        addHandler(elem, updatedChannelEvent, handler);
    }

    addButtonEvents(elem, elementSetting, channel) {
        const buttonAction = elementSetting.buttonAction;
        const channelTag = elementSetting.channelTag;
        const buttonInput = elem.querySelectorAll('[buttonInput="true"]')[0];

        const elementId = elem.getAttribute('id');

        if (channel) {
            const isMotor = isChannelMotorType(channel.fcuData.channelType);

            const handler = async (event: CustomEvent) => {
                const value = event.detail.value;
                const color = isMotor
                    ? this.colorDeciderService.getMotorChannelColor(value)
                    : this.colorDeciderService.getDigitalContactChannelColor(value);

                buttonInput.style.backgroundColor = color;
            };

            addHandler(elem, updatedChannelEvent, handler);
        }

        const disabledStatusHandler = (event: CustomEvent) => {
            const status = elementSetting.disabledStatus.status;
            const channelValue = event.detail.value;
            if (status === channelValue) {
                this.colorAnimationService.disabledElementsSet.add(elementId);
                buttonInput.style.backgroundColor = 'gray';
                buttonInput.style.opacity = '30%';
                buttonInput.setAttribute('disabled', 'true');
            } else if (buttonInput.getAttribute('disabled')) {
                this.colorAnimationService.disabledElementsSet.delete(elementId);
                buttonInput.style.backgroundColor = '#9cb2fb';
                buttonInput.style.opacity = '1';
                buttonInput.removeAttribute('disabled');
            }
        };

        const clickHandler = (event: CustomEvent) => {
            if (this.mimiActionsService.hasPendingResponse(elementId)) {
                return;
            }
            this.mimiActionsService.sendButtonAction(elementId, channelTag, buttonAction);
        };

        const flashingStausHandler = (event: CustomEvent) => {
            if (!this.colorAnimationService.disabledElementsSet.has(elementId)) {
                const start = elementSetting.flashingStatus.start;
                const finish = elementSetting.flashingStatus.finish;
                const flashingColor = elementSetting.flashingStatus.color;
                const channelValue = event.detail.value;
                if (start == channelValue) {
                    this.colorAnimationService.startFlashingButton(flashingColor, buttonInput, elementId);
                } else if (finish == channelValue) {
                    this.colorAnimationService.stopFlashingButton(elementId);
                    buttonInput.style.backgroundColor = '#9cb2fb';
                }
            }
        };

        addHandler(elem, disableStatusUpdated, disabledStatusHandler);
        addHandler(elem, click, clickHandler);
        addHandler(elem, flashingStatusUpdated, flashingStausHandler);
    }

    addToggleEvents(elem, elementSetting) {
        const span = elem.querySelectorAll('[span="true"]')[0];
        const input = elem.querySelectorAll('[input="true"]')[0];

        const elementId = elem.getAttribute('id');

        const updatedChannelHandler = (event: CustomEvent) => {
            if (this.colorAnimationService.disabledElementsSet.has(elementId)) {
                return;
            }

            if (!this.mimiActionsService.hasPendingResponse(elementId)) {
                const active = elementSetting.toggleState.active;
                const channelValue = event.detail.value;
                if (active == channelValue) {
                    span.style.left = '60%';
                    span.style.backgroundColor = '#9cb2fb';
                    input.style.backgroundColor = '#485573';
                } else {
                    span.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                    span.style.left = '7%';
                    input.style.backgroundColor = '#2c3a48';
                }
            }
        };

        const disableStatusHandler = (event: CustomEvent) => {
            const status = elementSetting.disabledStatus.status;
            const channelValue = event.detail.value;
            if (status === channelValue) {
                this.colorAnimationService.disabledElementsSet.add(elementId);
                span.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                input.style.backgroundColor = '#2c3a48';
                input.setAttribute('disabled', 'true');
            } else if (input.getAttribute('disabled')) {
                this.colorAnimationService.disabledElementsSet.delete(elementId);
                input.removeAttribute('disabled');
                if (span.style.left === '7%') {
                    input.style.backgroundColor = '#2c3a48';
                    span.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                } else if (span.style.left === '60%') {
                    span.style.backgroundColor = '#9cb2fb';
                    input.style.backgroundColor = '#485573';
                }
            }
        };

        const clickHandler = () => {
            if (this.mimiActionsService.hasPendingResponse(elementId)) {
                return;
            }
            const activeValue = elementSetting.toggleState.active;
            const inactiveValue = elementSetting.toggleState.inactive;
            if (span.style.left == '7%') {
                this.mimiActionsService.sendValue(elementId, elementSetting.channelTag, activeValue);
                span.style.left = '60%';
                span.style.backgroundColor = '#9cb2fb';
                input.style.backgroundColor = '#485573';
            } else if (span.style.left == '60%') {
                this.mimiActionsService.sendValue(elementId, elementSetting.channelTag, inactiveValue);
                span.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                span.style.left = '7%';
                input.style.backgroundColor = '#2c3a48';
            }
        };

        addHandler(elem, updatedChannelEvent, updatedChannelHandler);
        addHandler(elem, disableStatusUpdated, disableStatusHandler);
        addHandler(input, click, clickHandler);
    }

    async addStatusIndicatorEvents(elem, elementSettings, vesselId) {
        const statusIndicator = elem.querySelectorAll('[statusindicator="true"]')[0];
        const elementId = elem.getAttribute('id');

        const handler = async (event: CustomEvent) => {
            const channelValue = event.detail.value;
            const flag = event.detail;

            const elementClasses = this.colorAnimationService.getClassesByFlag(flag);
            if (this.colorAnimationService.hasFlagStatus(elementClasses)) {
                this.colorDeciderService.setAlarmFlagsColor(elementClasses, elem, null, statusIndicator);
                return;
            }
            this.colorDeciderService.stopAlarmFlagsColor(elementId);
            let color = this.colorDeciderService.getColorRanges(elementSettings.ranges, channelValue);
            if (color) {
                if (this.shouldUpdateColor(color, elementId)) {
                    this.colorDeciderService.currentFills.set(elementId, color);
                    elem.setAttribute('fill', color);
                }
                return;
            }

            if (elementSettings.channelIsDigital) {
                color = this.colorDeciderService.getDigitalContactChannelColor(channelValue);
            } else {
                color = this.colorDeciderService.getDefaultColor();
            }
            if (!this.shouldUpdateColor(color, elementId)) {
                return;
            }
            this.colorDeciderService.currentFills.set(elementId, color);
            statusIndicator.style.backgroundColor = color;
        };

        addHandler(elem, updatedChannelEvent, handler);
    }

    addInputEventListeners(elem, elementSetting, channel) {
        const buttonInput = elem.querySelectorAll('[buttonInput="true"]')[0];
        const inputContainer = elem.querySelectorAll('[inputContainer="true"]')[0];
        const input = elem.getElementsByTagName('INPUT')[0];

        const elementId = elem.getAttribute('id');

        const inputClickHandler = () => {
            input.select();
            input.style.color = 'white';
            inputContainer.style.border = '1px solid #9cb2fb';
        };

        const focusoutHandler = () => {
            inputContainer.style.border = '1px solid #cfd8dc';
            this.colorDeciderService.currentFills.delete(elementId);
        };

        const updatedChannelHandler = (event: CustomEvent) => {
            if (document.activeElement !== input) {
                input.value = String(this.formatInputValue(channel, event.detail.value));
            }

            const elementClasses = this.colorAnimationService.getClassesByFlag(event.detail);
            if (this.colorAnimationService.hasFlagStatus(elementClasses)) {
                this.colorDeciderService.setAlarmFlagsColor(elementClasses, elem, undefined, undefined, input);
                return;
            }
            this.colorDeciderService.stopAlarmFlagsColor(elementId);
            const color = this.colorDeciderService.getDefaultColor();
            if (this.shouldUpdateColor(color, elementId)) {
                this.colorDeciderService.currentFills.set(elementId, color);
                input.style.color = color;
            }
        };

        const disableStatusHandler = (event: CustomEvent) => {
            const status = elementSetting.disabledStatus.status;
            const channelValue = event.detail.value;
            if (status === channelValue) {
                this.colorAnimationService.disabledElementsSet.add(elementId);
                buttonInput.style.backgroundColor = 'gray';
                buttonInput.style.opacity = '30%';
                buttonInput.setAttribute('disabled', 'true');
            } else if (buttonInput.getAttribute('disabled')) {
                this.colorAnimationService.disabledElementsSet.delete(elementId);
                buttonInput.style.backgroundColor = '#9cb2fb';
                buttonInput.style.opacity = '1';
                buttonInput.removeAttribute('disabled');
            }
        };

        const buttonInputClickHandler = (event: CustomEvent) => {
            const value = input.value;
            if (!this.mimiActionsService.hasPendingResponse(elementId)) {
                this.mimiActionsService.sendValue(elementId, elementSetting.channelTag, value);
            }
        };

        const flashingStatusHandler = (event: CustomEvent) => {
            if (this.colorAnimationService.disabledElementsSet.has(elementId)) {
                return;
            }
            const start = elementSetting.flashingStatus.start;
            const finish = elementSetting.flashingStatus.finish;
            const flashingColor = elementSetting.flashingStatus.color;
            const channelValue = event.detail.value;
            if (start == channelValue) {
                this.colorAnimationService.startFlashingButton(flashingColor, buttonInput, elementId);
            } else if (finish == channelValue) {
                this.colorAnimationService.stopFlashingButton(elementId);
                buttonInput.style.backgroundColor = '#9cb2fb';
            }
        };

        addHandler(input, click, inputClickHandler);
        addHandler(input, 'focusout', focusoutHandler);
        if (channel) addHandler(elem, updatedChannelEvent, updatedChannelHandler);
        if (!buttonInput) return;
        addHandler(elem, disableStatusUpdated, disableStatusHandler);
        addHandler(buttonInput, click, buttonInputClickHandler);
        addHandler(elem, flashingStatusUpdated, flashingStatusHandler);
    }

    async addPumpEventListener(elem, channel) {
        if (channel) {
            const statusIndicator = elem.querySelectorAll('[statusindicator="true"]')[0];

            const isMotor = isChannelMotorType(channel.fcuData.channelType);

            const handler = async (event: CustomEvent) => {
                const value = event.detail.value;
                const color = isMotor
                    ? this.colorDeciderService.getMotorChannelColor(value)
                    : this.colorDeciderService.getDigitalContactChannelColor(value);

                const svgString = this.convertBackgroundSvgToBase64(statusIndicator);
                const svgContent = svgString
                    .replace(/fill-rule="evenodd"\s+fill=".*?"/g, `fill-rule="evenodd" fill="${color}"`)
                    .replace(/class="color"\s+fill=".*?"/g, `class="color" fill="${color}"`);

                const encodedSvg = `data:image/svg+xml;base64,${btoa(svgContent)}`;
                statusIndicator.style.background = `url(${encodedSvg})`;
                statusIndicator.style.backgroundSize = 'cover';
                statusIndicator.style.backgroundRepeat = 'no-repeat';
            };

            addHandler(elem, updatedChannelEvent, handler);
        }
    }

    async addFanEventListener(elem, channel) {
        if (channel) {
            const { statusStringContactOn, statusStringContactOff } = channel.fcuData.digitalAlarmSetting;

            const statusIndicator = elem.querySelectorAll('[statusindicator="true"]')[0];
            const statusString = elem.querySelectorAll('[statusstring="true"]')[0];

            const isMotor = isChannelMotorType(channel.fcuData.channelType);

            const handler = async (event: CustomEvent) => {
                const value = event.detail.value;
                const color = isMotor
                    ? this.colorDeciderService.getMotorChannelColor(value)
                    : this.colorDeciderService.getDigitalContactChannelColor(value);

                const svgString = this.convertBackgroundSvgToBase64(statusIndicator);
                const svgContent = svgString
                    .replace(/fill-rule="evenodd"\s+fill=".*?"/g, `fill-rule="evenodd" fill="${color}"`)
                    .replace(/class="color"\s+fill=".*?"/g, `class="color" fill="${color}"`);

                const encodedSvg = `data:image/svg+xml;base64,${btoa(svgContent)}`;
                statusIndicator.style.background = `url(${encodedSvg})`;
                statusIndicator.style.backgroundSize = 'cover';
                statusIndicator.style.backgroundRepeat = 'no-repeat';

                statusString.innerText = value ? statusStringContactOn : statusStringContactOff;
            };

            addHandler(elem, updatedChannelEvent, handler);
        }
    }

    async addGeneratorListener(elem, channel) {
        if (channel) {
            const statusIndicator = elem.querySelectorAll('[statusindicator="true"]')[0];

            const isMotor = isChannelMotorType(channel.fcuData.channelType);

            const handler = async (event: CustomEvent) => {
                const value = event.detail.value;
                const color = isMotor
                    ? this.colorDeciderService.getMotorChannelColor(value)
                    : this.colorDeciderService.getDigitalContactChannelColor(value);

                statusIndicator.style.backgroundColor = color;
            };

            addHandler(elem, updatedChannelEvent, handler);
        }
    }

    async addTankListener(elem, elementSetting, vesselId) {
        const maxUnits = elem.querySelectorAll('[maxUnits="true"]')[0];
        const minUnits = elem.querySelectorAll('[minUnits="true"]')[0];
        const unit = elem.querySelectorAll('[unit="true"]')[0];
        const unitValue = unit.innerText;

        const elementId = elem.getAttribute('id');
        const barFiller = elem.querySelectorAll('[barGraph="true"]')[0];

        const highHigh = elem.querySelectorAll('[highHigh="true"]')[0];
        const high = elem.querySelectorAll('[high="true"]')[0];
        const low = elem.querySelectorAll('[low="true"]')[0];
        const lowLow = elem.querySelectorAll('[lowLow="true"]')[0];

        const container = elem.querySelectorAll('.container')[0];
        const type = container.getAttribute('type');

        const value = elem.querySelectorAll('[value="true"]')[0];

        const handler = async (event: CustomEvent) => {
            const channelValue = event.detail.value;
            const channelRowId = event.detail.id;

            // const status = this.flagsService.getStatusByRowId(channelRowId);
            // value.innerText = status;
            unit.innerText = `${channelValue} ${unitValue}`;

            if (type !== TankTypes.auxiliaryAirReserve) {
                const HH = Number(highHigh.innerText);
                const H = Number(high.innerText);
                const L = Number(low.innerText);
                const LL = Number(lowLow.innerText);

                const min = Number(minUnits.getAttribute('value'));
                const max = Number(maxUnits.getAttribute('value'));

                let range;
                if (min < 0) range = max - min;
                else range = max + min;

                const area = this.createTankRanges({ min, LL, L, H, HH, max, range });
                this.setHlightedArea(channelValue, area, elem);
                const percentage = (channelValue / range) * 100 + '%';
                const flag = event.detail;

                barFiller.style.height = percentage;

                const elementClasses = this.colorAnimationService.getClassesByFlag(flag);
                if (this.colorAnimationService.hasFlagStatus(elementClasses)) {
                    this.colorDeciderService.setAlarmFlagsColor(elementClasses, elem, barFiller);
                    return;
                }
                this.colorDeciderService.stopAlarmFlagsColor(elementId);
                let color = this.colorDeciderService.getColorRanges(elementSetting.ranges, channelValue);
                if (color) {
                    if (this.shouldUpdateColor(color, elementId)) {
                        this.colorDeciderService.currentFills.set(elementId, color);
                        barFiller.style.backgroundColor = color;
                    }
                    return;
                }

                color = this.colorDeciderService.getDefaultBarColor();
                barFiller.style.backgroundColor = color;
                this.colorDeciderService.currentFills.set(elementId, color);
            } else {
                const flag = event.detail;

                const elementClasses = this.colorAnimationService.getClassesByFlag(flag);
                if (this.colorAnimationService.hasFlagStatus(elementClasses)) {
                    this.colorDeciderService.setAlarmFlagsColor(elementClasses, elem, barFiller);
                    return;
                }
                this.colorDeciderService.stopAlarmFlagsColor(elementId);
                let color = this.colorDeciderService.getColorRanges(elementSetting.ranges, channelValue);
                if (color) {
                    if (this.shouldUpdateColor(color, elementId)) {
                        this.colorDeciderService.currentFills.set(elementId, color);
                        barFiller.style.backgroundColor = color;
                    }
                    return;
                }
                color = this.colorDeciderService.getDefaultBarColor();
                barFiller.style.backgroundColor = color;
                this.colorDeciderService.currentFills.set(elementId, color);
            }
        };

        addHandler(elem, updatedChannelEvent, handler);
    }

    createTankRanges({ min, LL, L, H, HH, max, range }) {
        const ranges = [];
        const points = [];

        if (typeof min === 'number') points.push({ value: min, height: (min / range) * 100 });
        if (LL) points.push({ value: LL, height: (LL / range) * 100 });
        if (L) points.push({ value: L, height: (L / range) * 100 });
        if (H) points.push({ value: H, height: (H / range) * 100 });
        if (HH) points.push({ value: HH, height: (HH / range) * 100 });
        if (typeof max === 'number') points.push({ value: max, height: (max / range) * 100 });

        points.sort((a, b) => a.value - b.value);

        for (let i = 0; i < points.length - 1; i++) {
            ranges.push({
                min: points[i].value,
                max: points[i + 1].value,
                minHeight: points[i].height,
                maxHeight: points[i + 1].height,
            });
        }

        return ranges;
    }

    setHlightedArea(value, ranges, elem) {
        for (const range of ranges) {
            if (value >= range.min && value <= range.max) {
                const hlightedArea = elem.querySelectorAll('.hlighted-area')[0];
                hlightedArea.style.height = `${range.maxHeight - range.minHeight}%`;
                hlightedArea.style.bottom = `${range.minHeight}%`;
                hlightedArea.style.left = '0px';
            }
        }
    }

    convertBackgroundSvgToBase64(elem: HTMLElement) {
        const background = elem.style.background;

        const match = background.match(/url\("data:image\/svg\+xml[^"]*"\)/);
        const dataUrl = match[0].slice(5, -2);

        const base64String = dataUrl.split(',')[1];
        const svgString = atob(base64String);

        return svgString;
    }

    private formatInputValue(channel, value) {
        const channelType = channel.fcuData.channelType;
        if (isRunningHour(channelType)) return value;
        if (channelType == ChannelType.BLANK) return '';
        if ((!value && value != 0) || isNaN(value)) return ' - ';
        const currentValue = isDigital(channelType)
            ? value
                ? channel.fcuData.digitalAlarmSetting.statusStringContactOn
                : channel.fcuData.digitalAlarmSetting.statusStringContactOff
            : value / (channel.fcuData.scale || 1);
        return Number.isNaN(currentValue) ? ' - ' : currentValue;
    }

    private shouldUpdateColor(color, elementId): boolean {
        if (this.colorAnimationService.disabledElementsSet.has(elementId)) return false;
        return this.colorDeciderService.currentFills.get(elementId) != color;
    }

    async addVisualStateEventListener(elem, elementIntegration) {
        this.emitCreateLibraryComponentElement(elem, elementIntegration);
        const currentElement = elem;
        const libraryComponent = document.querySelectorAll(
            `[id="${elementIntegration.elementId}"][component="true"]`
        )[0] as any;
        const currentOpacity = elem.getAttribute('opacity') ? elem.getAttribute('opacity') : '1';

        const handler = (event: CustomEvent) => {
            const { detail: logicChannelList } = event;
            const operator = elementIntegration?.elementCondition?.operator;

            let conditionCount = 0;

            logicChannelList.forEach(logicChannel => {
                const logic = elementIntegration.elementCondition.logicList[logicChannel.index];
                const channel = logic.matrixChannel.channel;

                const { analogOperator, analogValue, digitalString } = logic;
                const scale = channel.fcuData.scale;

                if (analogOperator) {
                    const value = scale ? logicChannel.value / scale : logicChannel.value;
                    if (analogOperator === '<' && Number(analogValue) > value) {
                        conditionCount++;
                    } else if (analogOperator === '>' && Number(analogValue) < value) {
                        conditionCount++;
                    } else if (analogOperator === '<>') {
                        const numbers = analogValue.match(/-?\d+(\.\d+)?/g);
                        const min = Number(numbers?.[0]);
                        const max = Number(numbers?.[1]);
                        if (Number(min) > value && Number(max) > value) conditionCount++;
                    }
                } else if (digitalString) {
                    const { statusStringContactOn, statusStringContactOff } = channel.fcuData.digitalAlarmSetting;
                    const value = logicChannel.value;
                    if (statusStringContactOn === digitalString && value === 1) {
                        conditionCount++;
                    } else if (statusStringContactOff === digitalString && value === 0) {
                        conditionCount++;
                    }
                }
            });

            if (operator === 'AND') {
                if (conditionCount === logicChannelList.length) {
                    currentElement.style.opacity = '0';
                    libraryComponent.style.opacity = currentOpacity;
                } else {
                    currentElement.style.opacity = currentOpacity;
                    libraryComponent.style.opacity = '0';
                }
            } else if (operator === 'OR') {
                if (conditionCount) {
                    currentElement.style.opacity = '0';
                    libraryComponent.style.opacity = currentOpacity;
                } else {
                    currentElement.style.opacity = currentOpacity;
                    libraryComponent.style.opacity = '0';
                }
            }
        };

        addHandler(elem, visualStateEvent, handler);
    }

    private emitCreateLibraryComponentElement(elem, elementIntegration) {
        this._onCreateLibraryComponentElement.emit({ elem, elementIntegration });
    }

    onCreateLibraryComponentElement() {
        return this._onCreateLibraryComponentElement;
    }

    clear(): void {
        clearHandlers();
        this.colorDeciderService.clear();
    }
}
